import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, message } from 'antd';
import { FL150, l205Api } from '../../services/l205Api';
import { useL205Mode } from './hooks/useL205Mode';
import { useL205Record } from './hooks/useL205Record';
import { useL205Navigation } from './hooks/useL205Navigation';
import { useL205Permissions } from './hooks/useL205Permissions';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Details from './components/Tab2Details';
import ActionBar from './components/ActionBar';
import { isFieldDisabledFn } from './helpers';
import './L205Form.css';

const L205Form: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  // Hooks
  const perms = useL205Permissions();
  const { mode, setMode, isEditing } = useL205Mode();
  const {
    record, formData, details, selectedRow, loading,
    setSelectedRow,
    loadRecord, setField, getField,
    updateDetailRow,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult,
    getFieldError, clearFieldErrors,
  } = useL205Record();

  const loadRecordDirect = useCallback(async (id: string) => {
    await loadRecord(id);
  }, [loadRecord]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useL205Navigation(loadRecordDirect);

  // Initial Load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecord(ids[0]);
        }
      } catch {
        console.error('Failed to load L205 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadList, loadRecord, setCurrentIdx]);

  // Mode Actions
  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      // Save
      await handleSave(mode, async (result: FL150) => {
        if (mode === 'add') {
          const ids = await loadList();
          const newIdx = ids.indexOf(result.fl150_01);
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        }
        applyResult(result);
        clearFieldErrors();
        setMode('browse');
      });
    } else {
      // Enter add mode
      setMode('add');
      resetForAdd();
      setActiveTab(0);
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, setMode, resetForAdd, clearFieldErrors]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      // Revert
      revertToRecord();
      setMode('browse');
    } else {
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, setMode]);

  const handleDeleteAction = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除查檢表 ${record.fl150_01}？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const ids = await loadList();
          if (ids.length > 0) {
            const nextIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(nextIdx);
            await loadRecord(ids[nextIdx]);
          } else {
            resetForAdd();
            setCurrentIdx(-1);
          }
        }
      },
    });
  }, [record, isEditing, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecord, resetForAdd]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  // Copy handler
  const handleCopy = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認複製',
      content: `確定要複製查檢表 ${record.fl150_01}？`,
      okText: '複製',
      cancelText: '取消',
      onOk: async () => {
        try {
          // Copy locally: reset master fields, keep detail content
          setMode('add');
          const now = new Date();
          const month = now.getMonth() + 1;
          const period = month <= 6 ? 1 : 2;
          const isoDate = now.toISOString().substring(0, 10) + 'T00:00:00Z';
          setField('fl150_01', '' as FL150[keyof FL150]);
          setField('fl150_11', isoDate as FL150[keyof FL150]);
          setField('fl150_10', period as FL150[keyof FL150]);
          setField('fl150_12', '' as FL150[keyof FL150]);
          setField('fl150_13', '' as FL150[keyof FL150]);
          setField('fl150_81', '' as FL150[keyof FL150]);
          message.info('已複製，請編輯後存檔');
        } catch {
          message.error('複製失敗');
        }
      },
    });
  }, [record, isEditing, setMode, setField]);

  // Print handler (placeholder)
  const handlePrint = useCallback(() => {
    if (!record || isEditing) return;
    message.info('列印功能開發中');
  }, [record, isEditing]);

  const isFieldDisabled = useCallback(
    (key: string) => isFieldDisabledFn(key, isEditing, mode),
    [isEditing, mode]
  );

  // Render
  return (
    <PermissionGuard module={Modules.L205} action="query">
    <div className="l205-form">
      {loading && <div className="loading-overlay">載入中...</div>}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>品質管理</a>
              <span>/</span>
              <span className="current">ISO稽核查檢表</span>
            </nav>
            <span className="title-separator">--</span>
            <h1>ISO稽核查檢表</h1>
            <span className="badge badge-default">L205</span>
          </div>
        </div>

        {/* Search Bar */}
        <div className="search-bar">
          <div className="field-group">
            <label>查檢表號</label>
            <input
              type="text"
              readOnly
              style={{ width: 120 }}
              value={formData.fl150_01 || ''}
            />
          </div>
          <div className="field-group">
            <label>快速跳轉</label>
            <input
              type="text"
              style={{ width: 120 }}
              placeholder="輸入表號..."
              disabled={isEditing}
              onKeyDown={async (e) => {
                if (e.key === 'Enter') {
                  const target = e.currentTarget.value.trim();
                  if (!target) return;
                  const idx = allIds.indexOf(target);
                  if (idx >= 0) {
                    setCurrentIdx(idx);
                    await loadRecord(allIds[idx]);
                  } else {
                    // Try loading directly
                    try {
                      await loadRecord(target);
                      const ids = await loadList();
                      const newIdx = ids.indexOf(target);
                      if (newIdx >= 0) setCurrentIdx(newIdx);
                    } catch {
                      message.warning('查無此表號');
                    }
                  }
                }
              }}
            />
          </div>
          <div className="field-group">
            <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
              {allIds.length > 0 ? `第 ${currentIdx + 1} / ${allIds.length} 筆` : '無資料'}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs">
          <div className="tab-nav">
            {['基本資料', '查核明細'].map((label, i) => (
              <button
                key={i}
                className={`tab-nav-item ${activeTab === i ? 'active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Tab 1: 基本資料 */}
          <div className={`tab-pane ${activeTab === 0 ? 'active' : ''}`}>
            <Tab1BasicInfo
              formData={formData}
              isEditing={isEditing}
              isFieldDisabled={isFieldDisabled}
              getField={getField}
              setField={setField}
              getFieldError={getFieldError}
            />
          </div>

          {/* Tab 2: 查核明細 */}
          <div className={`tab-pane ${activeTab === 1 ? 'active' : ''}`}>
            <Tab2Details
              details={details}
              selectedRow={selectedRow}
              isEditing={isEditing}
              setSelectedRow={setSelectedRow}
              updateDetailRow={updateDetailRow}
              addDetailRow={addDetailRow}
              deleteDetailRow={deleteDetailRow}
            />
          </div>
        </div>

        {/* Bottom Action Bar (Pwtcmd1) */}
        <ActionBar
          mode={mode}
          isEditing={isEditing}
          record={record}
          currentIdx={currentIdx}
          allIdsLength={allIds.length}
          permissions={perms}
          onAdd={handleAddAction}
          onModify={handleModifyAction}
          onDelete={handleDeleteAction}
          onCopy={handleCopy}
          onPrint={handlePrint}
          onNavTo={handleNavTo}
          onExit={() => navigate('/erp')}
        />

      </div>{/* /page-wrapper */}
    </div>
    </PermissionGuard>
  );
};

export default L205Form;
